import { useRef } from "react";
import { CONFIG } from "../config.js";
import { positionForTitle } from "../metrics.ts";
import { todayISO, normalizeDate } from "../dates.ts";
import { parseCandidateTicket } from "../ticket.ts";
import { toast } from "../toast.ts";
import { Modal, TextField, PositionSelect } from "./Modal.tsx";
import type { EntryApi, RtoItem } from "../domain.ts";

export type LogRtoModalProps = {
  api: EntryApi;
  isDemo: boolean;
  onClose: () => void;
  /** Nạp lại list RTO sau khi lưu. */
  onSaved: () => Promise<void>;
};

export function LogRtoModal({ api, isDemo, onClose, onSaved }: LogRtoModalProps) {
  const today = todayISO();
  const formRef = useRef<HTMLFormElement | null>(null);

  // Dán bảng Candidate Info từ Teams → parse và điền form. CHỈ điền ô đang
  // trống, để không ghi đè thứ người dùng đã tự sửa. Đọc/ghi el.value trực
  // tiếp vì form là uncontrolled (xem Modal.tsx).
  const fillFromPaste = (text: string) => {
    const form = formRef.current;
    if (!form) return;
    const parsed = parseCandidateTicket(text);
    if (!Object.keys(parsed).length) return;

    const set = (name: string, val: string | undefined) => {
      const el = form.querySelector<HTMLInputElement>(`[name="${name}"]`);
      if (el && val && !el.value) el.value = val;
    };
    set("name", parsed.name);
    set("level", parsed.level);
    set("eng", parsed.eng);
    set("source", parsed.source);
    // Link không phải http(s) thì bỏ — ô này đi thẳng vào href.
    set("profileLink", /^https?:/i.test(parsed.profileLink || "") ? parsed.profileLink : "");
    set("interviewDate", normalizeDate(parsed.interviewDate || ""));
    set("round", parsed.round);
    set("interviewedBy", parsed.interviewedBy);
    set("assessment", parsed.assessment);
    set("joiningDate", parsed.joiningDate);
    set("potentialProject", parsed.potentialProject);

    // "Frontend Developer" → "Frontend Web" nhờ positions + aliases.
    const pos = parsed.position
      && positionForTitle(parsed.position, CONFIG.positions, CONFIG.stackAliases);
    const sel = form.querySelector<HTMLSelectElement>('[name="position"]');
    if (pos && sel) sel.value = pos;
  };

  return (
    <Modal
      title="Log Ready to Offer"
      onClose={onClose}
      formRef={formRef}
      onSubmit={async (fd) => {
        const text = (name: string) => String(fd.get(name) ?? "").trim();
        const entry: Omit<RtoItem, "id" | "status" | "recruiter"> = {
          rtoDate: text("rtoDate"),
          name: text("name"),
          position: text("position"),
          level: text("level"),
          eng: text("eng"),
          source: text("source"),
          profileLink: text("profileLink"),
          interviewDate: String(fd.get("interviewDate") ?? ""),
          round: text("round"),
          interviewedBy: text("interviewedBy"),
          assessment: text("assessment"),
          joiningDate: text("joiningDate"),
          potentialProject: text("potentialProject"),
        };
        if (!entry.name) throw new Error("Cần nhập tên ứng viên.");
        if (!entry.rtoDate) throw new Error("Cần chọn ngày ready to offer.");

        await api.addRtoEntry(entry);
        toast(isDemo ? "DEMO — đã thêm vào bộ nhớ (chưa lưu thật)." : "✓ Đã lưu vào SharePoint.");
        await onSaved();
      }}
    >
      <label className="f-field">
        <span>Dán bảng Candidate Info từ Teams (tự điền form)</span>
        <textarea
          name="paste" className="paste-box" rows={3}
          placeholder="Copy bảng CANDIDATE TICKET trên channel Teams rồi dán vào đây…"
          onChange={(e) => fillFromPaste(e.target.value)}
        />
      </label>
      <div className="f-row">
        <label className="f-field">
          <span>Ngày ready to offer</span>
          <input type="date" name="rtoDate" defaultValue={today} max={today} required />
        </label>
        <PositionSelect positions={CONFIG.positions} />
      </div>
      <div className="f-row">
        <label className="f-field">
          <span>Candidate name *</span>
          <input type="text" name="name" required placeholder="Nguyen Van X" />
        </label>
        <label className="f-field">
          <span>Technical level</span>
          <input type="text" name="level" placeholder="M2" />
        </label>
      </div>
      <div className="f-row">
        <TextField name="eng" label="ENG Score (ELSA)" placeholder="7.0" />
        <TextField name="source" label="Source" placeholder="HRHunt - An Nguyen" />
      </div>
      <TextField name="profileLink" label="Profile link" placeholder="https://…" />
      <div className="f-row">
        <label className="f-field">
          <span>Interview date</span>
          <input type="date" name="interviewDate" max={today} />
        </label>
        <TextField name="round" label="Round" placeholder="1 / ?" />
      </div>
      <div className="f-row">
        <TextField name="interviewedBy" label="Interviewed by" />
        <TextField name="joiningDate" label="Possible joining date" placeholder="~1 tháng" />
      </div>
      <TextField name="assessment" label="Overall assessment" />
      <TextField name="potentialProject" label="Potential project" />
      <p className="f-hint">
        Ứng viên sẽ vào benchmark "Ready to Offer vs KPI" và được đếm cho KPI tháng theo
        ngày ready to offer. Khi có kết quả, bấm Offer / Hire / ✕ ngay trên card ứng viên
        để cập nhật.
      </p>
    </Modal>
  );
}
